/**
 * Balance API client -- TanStack Query hook for the topbar balance
 * display. Sources the HUB wallet (owner rule: the hub balance is the
 * only balance shown; the upstream sendSMS balance is never surfaced).
 *
 * Endpoint wrapped:
 *   GET /api/wallet -> { balance_micro_eur, transactions }
 *
 * The wallet stores micro-EUR (1 EUR = 1_000_000 micro-EUR) to avoid
 * floating-point money bugs (banking-grade). We convert to EUR here at
 * the client boundary so consumers (AppLayout, StepperHeader) keep
 * receiving a plain EUR number -- they need no changes.
 *
 * Fail-closed: any error (not authed, wallet error) leaves data
 * undefined -- UI shows "—".
 */
import { useQuery } from "@tanstack/react-query";

const MICRO_EUR_PER_EUR = 1_000_000;

export type BalanceResponse = {
  balance: number; // EUR (converted from micro-EUR)
  billing_status: string; // D-SUSPEND-UX: drives the global suspension banner
  account_type?: string; // passthrough: server already sends it (LIM-VIZ)
  credit_limit: number; // EUR
};

type WalletApiResponse = {
  balance_micro_eur: number;
  account_type?: string;
  credit_limit_micro_eur?: number;
  billing_status?: string;
  transactions: unknown[];
};

async function fetchBalance(): Promise<BalanceResponse> {
  const res = await fetch("/api/wallet", { credentials: "include" });
  if (!res.ok) throw new Error("balance_unavailable");
  const data = (await res.json()) as WalletApiResponse;
  // Defensive: guard against non-numeric upstream shape (fail-closed).
  const micro =
    typeof data.balance_micro_eur === "number" ? data.balance_micro_eur : 0;
  const limitMicro = typeof data.credit_limit_micro_eur === "number" ? data.credit_limit_micro_eur : 0;
  return { balance: micro / MICRO_EUR_PER_EUR, billing_status: data.billing_status ?? "ok", account_type: data.account_type, credit_limit: limitMicro / MICRO_EUR_PER_EUR };
}

export function useBalance() {
  return useQuery({
    queryKey: ["wallet", "balance"],
    queryFn: fetchBalance,
    staleTime: 60_000,
    retry: false,
  });
}